import { createSelector } from "reselect"
import { ReduxState } from "../../common/types"
import { iCohortEmployee } from "../../cached-data/types"

/**
 * Selects the cohort employees entity state objects from Redux.
 */
const selectCohortEmployeesObjects = (state: ReduxState): Record<number, iCohortEmployee> => state.entities.cohortEmployees?.objects ?? {}

/**
 * Returns a mapping of employee ID to an array of cohort IDs that the employee
 * is a member of, derived from CohortEmployee records.
 *
 * This is distinct from iEmployee.cohort_permissions, which represents visibility
 * access (EmployeeCohortPermission), not membership (CohortEmployee).
 */
export const selectCohortIdsByEmployeeId = createSelector(
    [selectCohortEmployeesObjects],
    (cohortEmployees): Record<number, number[]> => {
        return Object.values(cohortEmployees).reduce<Record<number, number[]>>((acc, ce) => {
            if (!acc[ce.employee]) {
                acc[ce.employee] = []
            }
            acc[ce.employee].push(ce.cohort)
            return acc
        }, {})
    }
)

/**
 * Returns a mapping of cohort ID to an array of employee IDs that are members
 * of that cohort, derived from CohortEmployee records.
 *
 * This is the inverse of {@link selectCohortIdsByEmployeeId}.
 */
export const selectEmployeeIdsByCohortId = createSelector(
    [selectCohortEmployeesObjects],
    (cohortEmployees): Record<number, number[]> => {
        return Object.values(cohortEmployees).reduce<Record<number, number[]>>((acc, ce) => {
            if (!acc[ce.cohort]) {
                acc[ce.cohort] = []
            }
            acc[ce.cohort].push(ce.employee)
            return acc
        }, {})
    }
)
